// Bench del taller (955): formulario de pruebas según la plantilla del tipo de
// equipo (con pares antes/después), importación del log CSV de HWiNFO / MSI
// Afterburner y panel del certificado verificable (PDF del servidor, firmado
// con el .p12 de la empresa si lo tiene).
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Award, Copy, Download, FileUp, Mic, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, fieldCls, tallerRpc, QK } from './shared';

export interface CampoBench { clave: string; label: string; unidad?: string; tipo: 'numero' | 'texto' | 'si_no' | 'antes_despues'; menor_mejor?: boolean }
export type Pruebas = Record<string, unknown>;

export function usePlantillaBench(tipo: string) {
  return useQuery<CampoBench[]>({ queryKey: [QK, 'plantilla', tipo], queryFn: () => tallerRpc('bench_plantilla', { _tipo: tipo }), staleTime: Infinity });
}

/** Aviso del orbe de voz (el dictado escribe en el campo que tenga el foco). */
export function AvisoDictado() {
  return (
    <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
      <Mic className="h-3 w-3" /> Puede dictar: toque el campo y use el orbe de voz (el audio no sale del servidor).
    </p>
  );
}

// ── Log CSV de HWiNFO / Afterburner → mediciones ───────────────────────────
function parseCsv(texto: string): { cab: string[]; filas: string[][] } {
  const lineas = texto.split(/\r?\n/).filter((l) => l.trim());
  if (lineas.length < 2) return { cab: [], filas: [] };
  const sep = [',', ';', '\t'].map((s) => [s, lineas[0].split(s).length] as const).sort((a, b) => b[1] - a[1])[0][0];
  const split = (l: string) => l.split(sep).map((c) => c.replace(/^"|"$/g, '').trim());
  return { cab: split(lineas[0]), filas: lineas.slice(1).map(split) };
}
function columna(cab: string[], filas: string[][], prueba: (h: string) => boolean): number[] {
  const i = cab.findIndex((h) => prueba(h.toLowerCase()));
  if (i < 0) return [];
  return filas.map((f) => Number((f[i] ?? '').replace(',', '.'))).filter((n) => Number.isFinite(n) && n > 0);
}
const max = (v: number[]) => (v.length ? Math.round(Math.max(...v)) : null);
const prom = (v: number[]) => (v.length ? Math.round(v.reduce((a, b) => a + b, 0) / v.length) : null);
const low1 = (v: number[]) => (v.length ? Math.round([...v].sort((a, b) => a - b)[Math.floor(v.length * 0.01)]) : null);

/** Devuelve las mediciones leídas del log para la plantilla del tipo de equipo. */
export function medicionesDesdeLog(texto: string, tipo: string, momento: 'antes' | 'despues'): Pruebas {
  const { cab, filas } = parseCsv(texto);
  const cpu = columna(cab, filas, (h) => (h.includes('cpu') || h.includes('core') || h.includes('tctl') || h.includes('apu')) && (h.includes('°c') || h.includes('temp')));
  const gpu = columna(cab, filas, (h) => h.includes('gpu') && (h.includes('°c') || h.includes('temp')) && !h.includes('hot spot') && !h.includes('memory'));
  const fps = columna(cab, filas, (h) => h.includes('framerate') || h === 'fps' || h.includes('fps ') || h.includes('frame rate'));
  const pot = columna(cab, filas, (h) => (h.includes('power') || h.includes('potencia')) && h.includes('w'));
  const out: Pruebas = {};
  const put = (k: string, v: number | null) => { if (v != null) out[k] = v; };
  if (tipo === 'consola') {
    put(`temp_${momento}`, max(cpu.length ? cpu : gpu));
    if (momento === 'despues') { put('fps', prom(fps)); put('consumo', max(pot)); }
  } else {
    put(`temp_cpu_${momento}`, max(cpu));
    put(`temp_gpu_${momento}`, max(gpu));
    put(`fps_${momento}`, prom(fps));
    if (momento === 'despues') { put('fps_low', low1(fps)); put('consumo', max(pot)); }
  }
  return out;
}

// ── Formulario ─────────────────────────────────────────────────────────────
export function BenchForm({ tipo, valor, onChange, disabled = false, grande = false }: {
  tipo: string; valor: Pruebas; onChange: (v: Pruebas) => void; disabled?: boolean; grande?: boolean;
}) {
  const { data: plantilla = [] } = usePlantillaBench(tipo);
  const [momento, setMomento] = useState<'antes' | 'despues'>('despues');
  const archivo = useRef<HTMLInputElement>(null);
  const cls = `${fieldCls}${grande ? ' h-11 text-base' : ''}`;
  const txt = (k: string) => (valor[k] == null ? '' : String(valor[k]));
  const set = (k: string, v: unknown) => onChange({ ...valor, [k]: v === '' ? null : v });
  const num = (k: string, ph?: string) => (
    <input type="number" step="any" inputMode="decimal" className={cls} disabled={disabled} placeholder={ph} value={txt(k)}
      onChange={(e) => set(k, e.target.value === '' ? '' : Number(e.target.value))} />
  );
  const importar = async (f: File | undefined) => {
    if (!f) return;
    try {
      const m = medicionesDesdeLog(await f.text(), tipo, momento);
      if (!Object.keys(m).length) { toast.error('No se reconocieron columnas de temperatura, FPS o potencia en el archivo'); return; }
      onChange({ ...valor, ...m });
      toast.success(`Log importado como «${momento === 'antes' ? 'antes' : 'después'}»: ${Object.keys(m).length} medición(es). Revise y guarde.`);
    } catch (e) { toast.error(getErrorMessage(e)); } finally { if (archivo.current) archivo.current.value = ''; }
  };
  const conLog = ['consola', 'pc', 'laptop', 'gpu'].includes(tipo);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-semibold text-muted-foreground">Pruebas de banco (bench test)</div>
        {conLog && !disabled && (
          <div className="flex items-center gap-1 text-xs">
            <select className="rounded border bg-background px-1 py-0.5" value={momento} onChange={(e) => setMomento(e.target.value as 'antes' | 'despues')}>
              <option value="antes">Log de antes</option><option value="despues">Log de después</option>
            </select>
            <Button size="sm" variant="outline" onClick={() => archivo.current?.click()}><FileUp className="mr-1 h-3.5 w-3.5" /> Importar CSV (HWiNFO / Afterburner)</Button>
            <input ref={archivo} type="file" accept=".csv,.txt,text/csv" className="hidden" onChange={(e) => void importar(e.target.files?.[0])} />
          </div>
        )}
      </div>
      <div className={`grid gap-3 ${grande ? 'grid-cols-1' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
        {plantilla.map((c) => (
          c.tipo === 'antes_despues' ? (
            <Campo key={c.clave} label={`${c.label}${c.unidad ? ` (${c.unidad})` : ''}`}>
              <div className="flex gap-2">{num(`${c.clave}_antes`, 'antes')}{num(`${c.clave}_despues`, 'después')}</div>
            </Campo>
          ) : c.tipo === 'si_no' ? (
            <Campo key={c.clave} label={c.label}>
              <div className="flex gap-1">
                {([[true, 'Correcto'], [false, 'Con falla']] as const).map(([v, l]) => (
                  <button key={l} type="button" disabled={disabled} onClick={() => set(c.clave, valor[c.clave] === v ? '' : v)}
                    className={`flex-1 rounded-md border px-2 ${grande ? 'py-2.5 text-base' : 'py-1.5 text-sm'} ${valor[c.clave] === v ? (v ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white') : 'bg-background'}`}>{l}</button>
                ))}
              </div>
            </Campo>
          ) : c.tipo === 'numero' ? (
            <Campo key={c.clave} label={`${c.label}${c.unidad ? ` (${c.unidad})` : ''}`}>{num(c.clave)}</Campo>
          ) : (
            <Campo key={c.clave} label={c.label}><input className={cls} disabled={disabled} value={txt(c.clave)} onChange={(e) => set(c.clave, e.target.value)} /></Campo>
          )
        ))}
      </div>
    </div>
  );
}

// ── Certificado ────────────────────────────────────────────────────────────
interface EstadoCert { codigo: string; estado: 'pendiente' | 'generando' | 'firmado' | 'sin_firma' | 'error'; error: string | null; emitido_at: string | null; url: string }
const base = () => `${window.location.origin}${import.meta.env.BASE_URL.replace(/\/$/, '')}`;

export function descargarPdf(base64: string, nombre: string) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url; a.download = nombre; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function CertificadoBench({ ordenId, estadoOrden, guardarAntes }: { ordenId: string; estadoOrden: string; guardarAntes?: () => Promise<unknown> }) {
  const qc = useQueryClient();
  const { data: c } = useQuery<EstadoCert | null>({
    queryKey: [QK, 'bench', ordenId], queryFn: () => tallerRpc('bench_estado', { _orden: ordenId }),
    refetchInterval: (q) => (q.state.data && ['pendiente', 'generando'].includes(q.state.data.estado) ? 3000 : false),
  });
  const certificar = useMutation({
    mutationFn: async () => { if (guardarAntes) await guardarAntes(); return tallerRpc('bench_certificar', { _orden: ordenId, _base_url: base() }); },
    onSuccess: () => { toast.success('Certificado en preparación: el servidor genera el PDF'); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const [bajando, setBajando] = useState(false);
  const bajar = async () => {
    setBajando(true);
    try {
      const r = await tallerRpc<{ pdf: string; codigo: string } | null>('bench_pdf', { _orden: ordenId });
      if (r?.pdf) descargarPdf(r.pdf, `certificado-${r.codigo}.pdf`); else toast.error('El PDF aún no está listo');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setBajando(false); }
  };
  const prev = useRef(c?.estado);
  useEffect(() => {
    if (prev.current && ['pendiente', 'generando'].includes(prev.current) && c && ['firmado', 'sin_firma'].includes(c.estado)) toast.success('Certificado listo');
    prev.current = c?.estado;
  }, [c]);
  const puede = ['pruebas', 'listo', 'entregado'].includes(estadoOrden);
  const urlVer = c ? `${base()}/verificar-bench/${c.codigo}` : '';
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-violet-300 bg-violet-50 p-3 text-sm dark:border-violet-800 dark:bg-violet-950/30">
      <Award className="h-5 w-5 text-violet-700 dark:text-violet-300" />
      <div className="min-w-[200px] flex-1">
        <div className="font-semibold">Bench certificado</div>
        {!c && <div className="text-xs text-muted-foreground">{puede ? 'Complete las mediciones y emita el certificado verificable con QR (va al cliente y al pasaporte).' : 'Se certifica en pruebas, listo o entregado.'}</div>}
        {c && ['pendiente', 'generando'].includes(c.estado) && <div className="text-xs">Generando el PDF en el servidor…</div>}
        {c?.estado === 'firmado' && <div className="text-xs text-emerald-700 dark:text-emerald-300">Firmado electrónicamente · código {c.codigo}</div>}
        {c?.estado === 'sin_firma' && <div className="text-xs">Emitido sin firma (verificable por QR) · código {c.codigo}{c.error ? ` · ${c.error}` : ''}</div>}
        {c?.estado === 'error' && <div className="text-xs text-red-600">Error: {c.error}</div>}
      </div>
      {c && ['firmado', 'sin_firma'].includes(c.estado) && <>
        <Button size="sm" variant="outline" isLoading={bajando} onClick={() => void bajar()}><Download className="mr-1 h-3.5 w-3.5" /> PDF</Button>
        <Button size="sm" variant="outline" onClick={() => { void navigator.clipboard?.writeText(urlVer); toast.success('Enlace de verificación copiado'); }}><Copy className="mr-1 h-3.5 w-3.5" /> Verificación</Button>
      </>}
      {puede && <Button size="sm" isLoading={certificar.isPending} onClick={() => certificar.mutate()}>
        {c ? <><RefreshCw className="mr-1 h-3.5 w-3.5" /> Reemitir</> : <><Award className="mr-1 h-3.5 w-3.5" /> Certificar bench</>}</Button>}
    </div>
  );
}
